/* eslint-disable max-lines */
import {
  PersonMatchProfile,
  PersonProfileImageType,
  ProfileFormState,
  User,
  ValidationResult,
} from '@hakimo-ui/hakimo/types';
import { Column, Row, TableData } from '@hakimo-ui/hakimo/ui-table';
import { Label, Toggle, Tooltip } from '@hakimo-ui/shared/ui-base';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { PersonProfileFilters, ProfileType } from './types';

export const ADD_PROFILE_ID_PREFIX = 'profile-add-';
export const MAX_IMAGE_FILE_SIZE = 5;
export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 10;
export const MIN_AGE = 0;
export const PROFILE_IMAGE_GUIDELINES_LINK =
  'https://drive.google.com/file/d/15UpAPNUoiU8TPnl3hP_pypFdz6NjlQ6-/view';
export const VALIDATION_ERRORS = {
  NEGATIVE_AGE: `Age cannot be less than ${MIN_AGE}`,
  REQUIRED_FIRST_NAME: 'First name is required',
  REQUIRED_IMAGE: 'Primary image is required',
  REQUIRED_SECONDARY_IMAGE: 'Secondary image is required',
  IMAGE_TOO_LARGE: `Image file size should be less than ${MAX_IMAGE_FILE_SIZE} MB`,
};
export const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

export const PROFILE_TYPE_OPTIONS: {
  value: ProfileType | null;
  label: string;
}[] = [
  { value: null, label: 'All Profiles' },
  { value: 'BLACKLISTED', label: 'Unauthorized' },
  { value: 'BADGE', label: 'Badge' },
];

export const INITIAL_FORM_STATE: ProfileFormState = {
  first_name: '',
  last_name: '',
  age: '',
  gender: 'Male',
  employee_id: '',
  is_enabled: false,
  profile_type: 'BLACKLISTED',
};

export const columns: Column[] = [
  {
    id: 'image',
    name: 'Image',
  },
  {
    id: 'name',
    name: 'Name',
  },
  {
    id: 'profileType',
    name: 'Profile Type',
    showAtBreakpoint: 'md',
  },
  {
    id: 'employeeId',
    name: 'Employee ID',
    showAtBreakpoint: 'md',
  },
  {
    id: 'age',
    name: 'Age',
    showAtBreakpoint: 'lg',
  },
  {
    id: 'gender',
    name: 'Gender',
    showAtBreakpoint: 'lg',
  },
  {
    id: 'watchStatus',
    name: 'Status',
    showAtBreakpoint: 'md',
  },
];

export function getColumns(showBadgeId: boolean): Column[] {
  return columns.map((col) => {
    if (col.id === 'employeeId') {
      return {
        ...col,
        name: showBadgeId ? 'Badge ID' : 'Employee ID',
      };
    }
    return col;
  });
}

export function validateProfileForm(
  formData: Pick<ProfileFormState, 'first_name' | 'last_name' | 'age'>,
  imageFile?: File,
  isEdit = false,
  imagePreview?: string | null,
  secondaryImageFile?: File,
  fullBodyImageFile?: File
): ValidationResult {
  if (!formData.first_name.trim()) {
    return { isValid: false, message: VALIDATION_ERRORS.REQUIRED_FIRST_NAME };
  }

  if (formData.age.trim()) {
    const age = Number(formData.age);
    if (age < MIN_AGE) {
      return { isValid: false, message: VALIDATION_ERRORS.NEGATIVE_AGE };
    }
  }

  if (!isEdit && !imageFile) {
    return { isValid: false, message: VALIDATION_ERRORS.REQUIRED_IMAGE };
  }

  if (isEdit && !imageFile && !imagePreview) {
    return { isValid: false, message: VALIDATION_ERRORS.REQUIRED_IMAGE };
  }

  if (imageFile && getFileSizeInMB(imageFile) > MAX_IMAGE_FILE_SIZE) {
    return { isValid: false, message: VALIDATION_ERRORS.IMAGE_TOO_LARGE };
  }

  if (
    secondaryImageFile &&
    getFileSizeInMB(secondaryImageFile) > MAX_IMAGE_FILE_SIZE
  ) {
    return { isValid: false, message: VALIDATION_ERRORS.IMAGE_TOO_LARGE };
  }

  if (
    fullBodyImageFile &&
    getFileSizeInMB(fullBodyImageFile) > MAX_IMAGE_FILE_SIZE
  ) {
    return { isValid: false, message: VALIDATION_ERRORS.IMAGE_TOO_LARGE };
  }

  return { isValid: true, message: '' };
}

export function getFileSizeInMB(file: File): number {
  const sizeInBytes = file.size;
  const sizeInKB = sizeInBytes / 1024;
  const sizeInMB = sizeInKB / 1024;
  return sizeInMB;
}

export function getTableData(
  items: PersonMatchProfile[],
  onClickRow: (profile: PersonMatchProfile) => void,
  showBadgeId = false
): TableData {
  return {
    columns: getColumns(showBadgeId),
    rows: items.map((profile) =>
      createTableRow(profile, onClickRow, showBadgeId)
    ),
  };
}

function createTableRow(
  profile: PersonMatchProfile,
  onClickRow: (profile: PersonMatchProfile) => void,
  showBadgeId = false
): Row {
  return {
    id: profile.id,
    onClick: () => onClickRow(profile),
    cells: [
      {
        value: getProfileImage(profile),
      },
      {
        value:
          `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || '-',
      },
      {
        value: getProfileTypeLabel(profile.profileType),
      },
      {
        value: showBadgeId ? profile.badgeId || '-' : profile.employeeId || '-',
      },
      {
        value: profile.age && profile.age > MIN_AGE ? profile.age : '-',
      },
      {
        value: profile.gender || '-',
      },
      {
        value: getWatchStatusLabel(profile.isEnabled ?? false),
      },
    ],
  };
}

function getProfileImage(profile: PersonMatchProfile) {
  const imageUrl = profile.primaryImageURL;

  return imageUrl ? (
    <div className="border-onlight-line-3 dark:border-ondark-line-3 h-10 w-10 overflow-hidden rounded-full border-2">
      <img
        src={imageUrl}
        alt={`${profile.firstName || ''} ${profile.lastName || ''}`}
        className="h-10 w-10 rounded-full object-cover"
      />
    </div>
  ) : (
    <div className="bg-onlight-bg-3 dark:bg-ondark-bg-3 text-onlight-text-1 dark:text-ondark-text-1 flex h-10 w-10 items-center justify-center rounded-full">
      {profile.firstName ? profile.firstName.charAt(0) : ''}
      {profile.lastName ? profile.lastName.charAt(0) : ''}
    </div>
  );
}

function getWatchStatusLabel(isEnabled: boolean) {
  return isEnabled ? (
    <Label type="info" text="Enabled" small />
  ) : (
    <Label type="error" text="Disabled" small />
  );
}

function getProfileTypeLabel(profileType?: string) {
  if (!profileType) return '-';
  switch (profileType) {
    case 'BLACKLISTED':
      return 'Unauthorized';
    case 'WHITELISTED':
      return 'Authorized';
    case 'BADGE':
      return 'Badge';
    default:
      return profileType;
  }
}

export interface WatchStatusToggleProps {
  enabled: boolean;
  onChange: (checked: boolean) => void;
}

export function WatchStatusToggle({
  enabled,
  onChange,
}: WatchStatusToggleProps) {
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-2">
        <Toggle enabled={enabled} onChange={onChange} label="Enabled" />
        <Tooltip
          text="Activates monitoring / lookout for this profile"
          colorModifier="info"
          position="top-right"
          size="medium"
        >
          <InformationCircleIcon className="text-onlight-text-3 dark:text-ondark-text-3 h-5 w-5" />
        </Tooltip>
      </div>
      <a
        href={PROFILE_IMAGE_GUIDELINES_LINK}
        target="_blank"
        rel="noreferrer"
        className="text-primary-600 dark:text-primary-400 text-sm"
      >
        Guidelines to Add Images
      </a>
    </div>
  );
}

export function hasFiltersApplied(filters: PersonProfileFilters): boolean {
  return (
    filters.profileType?.value !== undefined ||
    (filters.employeeId?.value && filters.employeeId.value.length > 0) ||
    (filters.badgeId?.value && filters.badgeId.value.length > 0) ||
    filters.status?.isEnabled !== undefined
  );
}

export function getPersonProfileSearchParams(
  filters: PersonProfileFilters,
  page?: number,
  pageSize?: number,
  searchName?: string,
  showBadgeId = false
): string {
  const params = new URLSearchParams();
  if (page) {
    params.append('page', page.toString());
  }
  if (pageSize) {
    params.append('pageSize', pageSize.toString());
  }

  if (filters.profileType?.value) {
    params.append('profile_type', filters.profileType.value);
  }

  if (showBadgeId && filters.badgeId?.value?.trim()) {
    params.append('badge_id', filters.badgeId.value.trim());
  } else if (!showBadgeId && filters.employeeId?.value?.trim()) {
    params.append('employee_id', filters.employeeId.value.trim());
  }

  if (filters.status?.isEnabled !== undefined) {
    params.append('isEnabled', filters.status.isEnabled.toString());
  }

  if (searchName && searchName.trim()) {
    params.append('name', searchName.trim());
  }

  return params.toString();
}

export function createValueUpdater(
  setFormState: (value: React.SetStateAction<ProfileFormState>) => void,
  setValidationState: (value: ValidationResult) => void
) {
  return (field: keyof ProfileFormState) =>
    (arg: React.ChangeEvent<HTMLInputElement> | string | boolean) => {
      setValidationState({ isValid: true, message: '' });

      if (typeof arg === 'boolean') {
        setFormState((prev: ProfileFormState) => ({ ...prev, [field]: arg }));
      } else if (typeof arg === 'string') {
        setFormState((prev: ProfileFormState) => ({ ...prev, [field]: arg }));
      } else {
        setFormState((prev: ProfileFormState) => ({
          ...prev,
          [field]: arg.target.value,
        }));
      }
    };
}

export function validateTenantAccess(
  user: User,
  tenantId: string
): ValidationResult {
  if (!tenantId) {
    return {
      isValid: false,
      message: 'Unable to determine location tenant ID. Please try again.',
    };
  }

  const userTenants = [user.tenant.id];
  if (user.mspTenants) {
    user.mspTenants.forEach((tenant) => userTenants.push(tenant.id));
  }
  if (!userTenants.includes(tenantId)) {
    return {
      isValid: false,
      message: 'You have no permission to create profiles for this location.',
    };
  }

  return { isValid: true, message: '' };
}

const IMAGE_TYPE_TO_FORM_FIELD: Record<
  PersonProfileImageType,
  'primary_image' | 'secondary_image' | 'full_body_image'
> = {
  primary: 'primary_image',
  secondary: 'secondary_image',
  full_body: 'full_body_image',
};

export function handleImageUpload(
  file: File,
  setFormState: (
    value: ProfileFormState | ((prev: ProfileFormState) => ProfileFormState)
  ) => void,
  setImagePreview: (value: string | null) => void,
  setValidationState?: (value: ValidationResult) => void,
  imageType: PersonProfileImageType = 'primary'
) {
  const imageField = IMAGE_TYPE_TO_FORM_FIELD[imageType];
  setFormState((prev: ProfileFormState) => ({
    ...prev,
    [imageField]: file,
  }));
  setImagePreview(URL.createObjectURL(file));
  if (setValidationState) {
    setValidationState({ isValid: true, message: '' });
  }
}

export function handleImageRemove(
  setImagePreview: (value: string | null) => void,
  setFormState: (
    value: ProfileFormState | ((prev: ProfileFormState) => ProfileFormState)
  ) => void,
  setValidationState?: (value: ValidationResult) => void,
  imageType: PersonProfileImageType = 'primary'
) {
  const imageField = IMAGE_TYPE_TO_FORM_FIELD[imageType];
  setImagePreview(null);
  setFormState((prev: ProfileFormState) => ({
    ...prev,
    [imageField]: undefined,
  }));
  if (setValidationState) {
    setValidationState({ isValid: true, message: '' });
  }
}
